import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Plane, 
  Download, 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  CreditCard,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader,
  Mail,
  Phone,
  Unlock
} from 'lucide-react';
import axios from 'axios';
import { fetchAirlineLogos } from '../utils/airlineLogoCache';
import { getUserFacingBookingId } from '../utils/userFacingBookingId';
import PaymentAndRefundCard from '../components/PaymentAndRefundCard';
import BookingStatusChips from '../components/booking/BookingStatusChips';
import {
  getLegBaggageRowsForBooking,
  getNonBaggageSsrRowsForBooking,
} from '../utils/ssrBreakdown';
import { useAuth } from '../context/AuthContext';
import { useAgentAuth } from '../context/AgentAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isAuthenticated: isAgentAuthenticated, loading: agentAuthLoading } = useAgentAuth();
  
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [error, setError] = useState(null);
  const [airlineLogos, setAirlineLogos] = useState({});
  const [invoiceMeta, setInvoiceMeta] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [releasingPnr, setReleasingPnr] = useState(false);

  useEffect(() => {
    // Wait until both auth contexts have finished hydrating before deciding to redirect.
    if (authLoading || agentAuthLoading) return;
    if (!isAuthenticated && !isAgentAuthenticated) {
      navigate('/');
      return;
    }
    fetchBookingDetails();
  }, [bookingId, authLoading, agentAuthLoading, isAuthenticated, isAgentAuthenticated]);

  const fetchBookingDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use unified endpoint that handles both Tripjack and Offline bookings
      const response = await axios.get(
        `${BACKEND_URL}/api/bookings/${bookingId}`,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const booking = response.data.data;
        setBookingData(booking);

        // Fetch tax-invoice metadata in parallel (agent-only endpoint).
        // Silent failure is OK — invoice is non-blocking and B2C users don't have one.
        if (isAgentAuthenticated) {
          axios.get(
            `${BACKEND_URL}/api/agent/dashboard/bookings/${bookingId}/invoice`,
            { withCredentials: true, validateStatus: () => true }
          ).then(r => {
            if (r.status >= 200 && r.status < 300 && r.data && r.data.available !== false) {
              setInvoiceMeta(r.data.invoice || r.data);
            }
          }).catch(() => { /* silent — invoice optional */ });
        }
        
        // Fetch airline tail logos (Logostream, cached via utils/airlineLogoCache)
        if (booking.flights && booking.flights.length > 0) {
          const airlineCodes = booking.flights
            .map(f => f.airlineCode)
            .filter(code => code && code !== '');

          if (airlineCodes.length > 0) {
            try {
              const data = await fetchAirlineLogos(airlineCodes);
              setAirlineLogos(prev => ({ ...prev, ...data }));
            } catch (logoErr) {
              console.error('Error fetching airline logos:', logoErr);
              // Continue without logos - not a critical error
            }
          }
        }
      } else {
        setError('Booking not found');
      }
    } catch (err) {
      console.error('Error fetching booking details:', err);
      setError(err.response?.data?.detail || 'Failed to fetch booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadEticket = async () => {
    setDownloadingPdf(true);
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/bookings/download-eticket/${bookingId}`,
        {
          responseType: 'blob',
          withCredentials: true
        }
      );
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eticket_${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading e-ticket:', err);
      alert('Failed to download e-ticket. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Tax-invoice download (agent-only, only when invoice metadata loaded)
  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent/dashboard/bookings/${bookingId}/invoice/download`,
        { responseType: 'blob', withCredentials: true }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceMeta?.invoice_number || bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading invoice:', err);
      alert('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleReleaseHoldPnr = async () => {
    const tjId =
      bookingData?.tripjack_booking_id
      || bookingData?.supplier_booking_id
      || (String(bookingData?.bookingId || '').startsWith('TJ')
        ? bookingData.bookingId
        : null);
    if (!tjId) {
      alert('Hold cannot be released: supplier reference is missing. Please contact support.');
      return;
    }
    if (!window.confirm(
      'Release this hold PNR? Seats return to inventory and the airline will mark the order UNCONFIRMED.'
    )) {
      return;
    }
    setReleasingPnr(true);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/tripjack/release-pnr`,
        { bookingId: tjId },
        { withCredentials: true }
      );
      if (resp.data?.success !== false) {
        alert(resp.data?.message || 'Hold released successfully.');
        await fetchBookingDetails();
      } else {
        alert('Release did not complete. Please try again or use Admin bookings.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to release hold';
      console.error('release-pnr:', err);
      alert(String(msg));
    } finally {
      setReleasingPnr(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'SUCCESS': { 
        color: 'bg-green-100 text-green-700 border-green-200', 
        icon: CheckCircle,
        label: 'Confirmed'
      },
      'CONFIRMED': { 
        color: 'bg-green-100 text-green-700 border-green-200', 
        icon: CheckCircle,
        label: 'Confirmed'
      },
      'PENDING': { 
        color: 'bg-yellow-100 text-yellow-700 border-yellow-200', 
        icon: Clock,
        label: 'Pending'
      },
      'CANCELLED': { 
        color: 'bg-red-100 text-red-700 border-red-200', 
        icon: AlertCircle,
        label: 'Cancelled'
      },
      'ON_HOLD': { 
        color: 'bg-blue-100 text-blue-700 border-blue-200', 
        icon: Clock,
        label: 'On Hold'
      },
      'HOLD': {
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: Clock,
        label: 'On Hold'
      },
      'RELEASED': {
        color: 'bg-gray-100 text-gray-700 border-gray-200',
        icon: AlertCircle,
        label: 'Released'
      }
    };
    return configs[status?.toUpperCase()] || configs['PENDING'];
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr || dateTimeStr === 'N/A') return 'N/A';
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateTimeStr;
    }
  };

  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr || dateTimeStr === 'N/A') return 'N/A';
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateTimeStr;
    }
  };

  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr || dateTimeStr === 'N/A') return 'N/A';
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateTimeStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-[#335E77]" />
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-[#335E77] text-white rounded-lg hover:bg-[#1B375B] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!bookingData) return null;

  const rawHoldDeadline =
    bookingData.holdTimeLimit ?? bookingData.hold_until ?? bookingData.hold_time_limit;
  const hasHoldDeadline =
    rawHoldDeadline != null && String(rawHoldDeadline).trim() !== '';
  const supplierOrderUpper = String(
    bookingData.supplier_order_status
    || bookingData.fullDetails?.order?.status
    || bookingData.tripjack_response?.order?.status
    || ''
  ).toUpperCase();
  const acfStatusLower = String(bookingData.status || '').toLowerCase();
  const sourceLower = String(bookingData.source || '').toLowerCase();
  const bookingModeLower = String(bookingData.bookingMode || '').toLowerCase();
  const isTripjackSource = sourceLower === 'tripjack';
  const acfHoldLike = ['on_hold', 'hold'].includes(acfStatusLower);
  const supplierLooksLikeHold = ['ON_HOLD', 'HOLD'].includes(supplierOrderUpper);
  const dbHoldFlag = bookingData.is_hold === true || bookingModeLower === 'hold';
  const supplierTicketedOrFinal = ['CONFIRMED', 'SUCCESS', 'TICKETED'].includes(supplierOrderUpper);
  const acfPaymentSettled = ['confirmed', 'success', 'ticketed'].includes(acfStatusLower);
  const isLiveHoldContext =
    isTripjackSource
    && !supplierTicketedOrFinal
    && !acfPaymentSettled
    && acfStatusLower !== 'released'
    && (acfHoldLike || supplierLooksLikeHold || dbHoldFlag);
  const showHoldDeadlineBanner = hasHoldDeadline && isLiveHoldContext;
  const showHoldMissingDeadlineBanner = !hasHoldDeadline && isLiveHoldContext;
  const tripjackReleaseId =
    bookingData.tripjack_booking_id
    || bookingData.supplier_booking_id
    || (String(bookingData.bookingId || '').startsWith('TJ') ? bookingData.bookingId : null);
  const showReleaseHoldButton =
    isAgentAuthenticated
    && isTripjackSource
    && !!tripjackReleaseId
    && !supplierTicketedOrFinal
    && !acfPaymentSettled
    && acfStatusLower !== 'released'
    && supplierOrderUpper !== 'UNCONFIRMED'
    && (
      supplierLooksLikeHold
      || acfHoldLike
      || (acfStatusLower === 'pending' && dbHoldFlag)
    );

  const formatSupplierHoldDeadline = (raw) => {
    if (raw == null || raw === '') return '';
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return String(raw);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Booking Details</h1>
              <p className="text-gray-500">Booking ID: {getUserFacingBookingId(bookingData)}</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDownloadEticket}
                disabled={downloadingPdf}
                data-testid="download-eticket-btn"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#335E77] text-white rounded-lg hover:bg-[#1B375B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingPdf ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Download E-Ticket</span>
                  </>
                )}
              </button>

              {showReleaseHoldButton && (
                <button
                  type="button"
                  onClick={handleReleaseHoldPnr}
                  disabled={releasingPnr}
                  data-testid="booking-details-release-hold-btn"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {releasingPnr ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Releasing…</span>
                    </>
                  ) : (
                    <>
                      <Unlock className="w-5 h-5" />
                      <span>Release hold (unhold)</span>
                    </>
                  )}
                </button>
              )}

              {/* Tax Invoice (agent-only, only when invoice metadata is available) */}
              {isAgentAuthenticated && invoiceMeta && (
                <button
                  onClick={handleDownloadInvoice}
                  disabled={downloadingInvoice}
                  data-testid="download-invoice-btn"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#335E77] border-2 border-[#335E77] rounded-lg hover:bg-[#335E77] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingInvoice ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      <span>Download Invoice</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Booking Status Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <BookingStatusChips
              consumerStatus={bookingData.consumer_status}
              booking={bookingData}
              layout="detail"
            />

            <div>
              <p className="text-sm text-gray-500 mb-1">PNR</p>
              <p className="text-lg font-bold text-gray-900">{bookingData.pnr || 'N/A'}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500 mb-1">Booking Date</p>
              <p className="text-lg font-semibold text-gray-900">{formatDate(bookingData.createdOn)}</p>
            </div>
          </div>

          {/* Sprint May-2026 · Seat warning banner. Surfaces when Tripjack
              dropped one or more requested seats (errCode 1131 retry OR
              silent drop). Persisted on booking_record.seat_warning by
              tripjack_booking.py so it lights up across agent/admin/B2C
              booking-details surfaces consistently. */}
          {bookingData.seat_warning && (
            <div
              data-testid="seat-warning-banner"
              className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <strong>Seat preference:</strong> {bookingData.seat_warning}
              </p>
            </div>
          )}

          {showHoldDeadlineBanner && (
            <div
              data-testid="hold-pay-before-banner"
              className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3"
            >
              <Clock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-950">
                <p className="font-semibold mb-1">Hold — pay &amp; confirm before</p>
                <p className="text-amber-900">
                  <strong>{formatSupplierHoldDeadline(rawHoldDeadline)}</strong>
                  <span className="text-amber-800"> (supplier-quoted)</span>
                </p>
              </div>
            </div>
          )}
          {showHoldMissingDeadlineBanner && (
            <div
              data-testid="hold-no-deadline-banner"
              className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
            >
              <Clock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-950">
                <p className="font-semibold mb-1">Hold booking</p>
                <p className="text-amber-900">
                  Tripjack did not return a pay-by time limit for this record. Confirm payment or release the hold before the airline or supplier policy expires.
                </p>
              </div>
            </div>
          )}

          {/* Audit §7 L1 — Surface supplier `messages[]` from /review when
              persisted on the booking. Tripjack ships short airline / fare
              remarks (e.g. "Baggage allowance subject to airline policy")
              that we previously dropped silently. */}
          {Array.isArray(bookingData.supplierMessages) && bookingData.supplierMessages.length > 0 ? (
            <div
              data-testid="booking-supplier-messages"
              className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4"
            >
              <p className="text-xs font-semibold text-blue-900 mb-2">Airline / supplier notes</p>
              <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside leading-snug">
                {bookingData.supplierMessages.map((m, mi) => (
                  <li key={mi}>{typeof m === 'string' ? m : (m?.message || JSON.stringify(m))}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Audit §7 L2 — Per-pax status map (CANCELLED / VOIDED / NO_SHOW
              / REISSUED). Tripjack ships these on `statusMap` after partial
              voids / no-shows. Surfacing keeps support tickets short. */}
          {bookingData.statusMap && typeof bookingData.statusMap === 'object' && Object.keys(bookingData.statusMap).length > 0 ? (
            <div
              data-testid="booking-status-map"
              className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <p className="text-xs font-semibold text-gray-700 mb-2">Per-passenger status</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(bookingData.statusMap).map(([paxRef, st]) => {
                  const stCode = String(st || '').toUpperCase();
                  const tone =
                    stCode === 'CONFIRMED' || stCode === 'BOOKED' || stCode === 'TICKETED'
                      ? 'text-green-700 bg-green-100'
                      : stCode === 'CANCELLED' || stCode === 'VOIDED' || stCode === 'NO_SHOW'
                      ? 'text-red-700 bg-red-100'
                      : stCode === 'REISSUED'
                      ? 'text-amber-800 bg-amber-100'
                      : 'text-gray-700 bg-gray-200';
                  return (
                    <div key={paxRef} className="text-xs flex items-center gap-2">
                      <span className="font-mono text-gray-600">{paxRef}</span>
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${tone}`}>{stCode || 'UNKNOWN'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        {bookingData.flights && bookingData.flights.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-testid="flight-itinerary-card">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Plane className="w-6 h-6 text-[#335E77]" />
              Flight Itinerary
              {bookingData.isRoundTrip && (
                <span data-testid="round-trip-badge" className="text-xs font-semibold bg-[#335E77]/10 text-[#335E77] px-2 py-0.5 rounded-full">
                  Round Trip
                </span>
              )}
            </h2>

            <div className="space-y-4">
              {bookingData.flights.map((flight, index) => {
                const airlineLogo = airlineLogos[flight.airlineCode]?.best || airlineLogos[flight.airlineCode]?.rectangular;
                const isReturn = (flight.leg === 'return') || (bookingData.isRoundTrip && index === 1);
                const legLabel = isReturn ? 'Return' : (bookingData.isRoundTrip ? 'Onward' : null);
                const legPnr = isReturn ? (bookingData.returnPnr || bookingData.pnr) : bookingData.pnr;
                const legAirlinePnr = isReturn ? (bookingData.returnAirlinePnr || bookingData.airlinePnr) : bookingData.airlinePnr;
                return (
                  <div
                    key={index}
                    data-testid={`flight-leg-${flight.leg || (index === 0 ? 'onward' : 'return')}`}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    {legLabel && (
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                        <span className="text-xs font-bold uppercase tracking-wider text-[#335E77]">
                          {legLabel} Leg
                        </span>
                        <div className="text-right text-xs">
                          {legPnr && legPnr !== 'N/A' && (
                            <span className="text-gray-600">
                              PNR: <span className="font-mono font-semibold text-gray-900">{legPnr}</span>
                            </span>
                          )}
                          {legAirlinePnr && (
                            <span className="text-gray-600 ml-3">
                              Airline PNR: <span className="font-mono font-semibold text-gray-900">{legAirlinePnr}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                          {airlineLogo ? (
                            <img
                              src={airlineLogo}
                              alt={flight.airline}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <Plane
                            className="w-6 h-6 text-[#335E77]"
                            style={{ display: airlineLogo ? 'none' : 'block' }}
                          />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{flight.airline}</p>
                          <p className="text-sm text-gray-500">{flight.flightNumber}</p>
                          {flight.isCodeshare && flight.operatingAirlineCode && (
                            <p
                              data-testid={`booking-codeshare-leg-${flight.leg || index}`}
                              className="text-xs text-gray-600 mt-0.5"
                            >
                              Operated by{' '}
                              <span className="font-semibold text-gray-900">
                                {flight.operatingAirline || flight.operatingAirlineCode}
                              </span>
                            </p>
                          )}
                          {flight.stops && flight.stops !== 'Non Stop' && (
                            <p className="text-xs text-amber-700 font-medium">{flight.stops}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Class</p>
                        <p className="font-semibold text-gray-900">{flight.class}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">From</p>
                        <p className="text-lg font-bold text-gray-900">{flight.origin}</p>
                        {(flight.originCity || flight.originAirportName) && (
                          <p className="text-sm font-medium text-gray-700 mt-0.5">
                            {flight.originCity}
                            {flight.originCity && flight.originAirportName ? ' • ' : ''}
                            {flight.originAirportName}
                          </p>
                        )}
                        {flight.originTerminal && (
                          <p className="text-xs text-gray-500">Terminal {flight.originTerminal}</p>
                        )}
                        <p className="text-sm text-gray-600 mt-2">{formatTime(flight.departureTime)}</p>
                        <p className="text-xs text-gray-500">{formatDate(flight.departureTime)}</p>
                      </div>

                      <div className="flex items-center justify-center">
                        <div className="text-center">
                          <Clock className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                          <p className="text-sm text-gray-600">{flight.duration || 'N/A'}</p>
                          {flight.stops && (
                            <p className="text-xs text-gray-400 mt-1">{flight.stops}</p>
                          )}
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">To</p>
                        <p className="text-lg font-bold text-gray-900">{flight.destination}</p>
                        {(flight.destinationCity || flight.destinationAirportName) && (
                          <p className="text-sm font-medium text-gray-700 mt-0.5">
                            {flight.destinationCity}
                            {flight.destinationCity && flight.destinationAirportName ? ' • ' : ''}
                            {flight.destinationAirportName}
                          </p>
                        )}
                        {flight.destinationTerminal && (
                          <p className="text-xs text-gray-500">Terminal {flight.destinationTerminal}</p>
                        )}
                        <p className="text-sm text-gray-600 mt-2">{formatTime(flight.arrivalTime)}</p>
                        <p className="text-xs text-gray-500">{formatDate(flight.arrivalTime)}</p>
                      </div>
                    </div>

                    {/* Connecting-flight per-segment breakdown */}
                    {flight.segments && flight.segments.length > 1 && (
                      <div className="mt-3 pt-3 border-t border-gray-200" data-testid={`flight-segments-${flight.leg || index}`}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Segments</p>
                        <div className="space-y-2">
                          {flight.segments.map((s, si) => (
                            <div key={si} className="flex items-center gap-3 text-xs text-gray-700">
                              <span className="font-mono font-semibold text-gray-900 w-16">{s.flightNumber}</span>
                              <span className="font-bold">{s.origin}</span>
                              <span className="text-gray-400">→</span>
                              <span className="font-bold">{s.destination}</span>
                              <span className="text-gray-500">{formatTime(s.departureTime)} → {formatTime(s.arrivalTime)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Passenger Details */}
        {bookingData.travellers && bookingData.travellers.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-6 h-6 text-[#335E77]" />
              Passenger Details
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                    {bookingData.isRoundTrip && bookingData.returnPnr ? (
                      <>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Onward PNR / Ticket</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Return PNR / Ticket</th>
                      </>
                    ) : (
                      <>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">PNR</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Ticket Number</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {bookingData.travellers.map((traveller, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`traveller-row-${index}`}>
                      <td className="py-3 px-4 text-sm text-gray-900 font-medium">{traveller.name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{traveller.type}</td>
                      {bookingData.isRoundTrip && bookingData.returnPnr ? (
                        <>
                          <td className="py-3 px-4 text-xs text-gray-600 font-mono">
                            <div className="font-semibold">{traveller.pnr || 'N/A'}</div>
                            <div className="text-gray-500">{traveller.ticketNumber || 'N/A'}</div>
                          </td>
                          <td className="py-3 px-4 text-xs text-gray-600 font-mono">
                            <div className="font-semibold">{traveller.returnPnr || traveller.pnr || 'N/A'}</div>
                            <div className="text-gray-500">{traveller.returnTicketNumber || 'N/A'}</div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-3 px-4 text-sm text-gray-600 font-mono">{traveller.pnr || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 font-mono">{traveller.ticketNumber || 'N/A'}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contact Details */}
        {(bookingData.contact_email || bookingData.contact_phone) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-6 h-6 text-[#335E77]" />
              Contact Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bookingData.contact_email && (
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-200">
                    <Mail className="w-5 h-5 text-[#335E77]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <a
                      href={`mailto:${bookingData.contact_email}`}
                      className="text-sm font-medium text-gray-900 hover:text-[#335E77] break-all"
                    >
                      {bookingData.contact_email}
                    </a>
                  </div>
                </div>
              )}
              {bookingData.contact_phone && (
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-200">
                    <Phone className="w-5 h-5 text-[#335E77]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-1">Phone</p>
                    <a
                      href={`tel:${bookingData.contact_phone}`}
                      className="text-sm font-medium text-gray-900 hover:text-[#335E77]"
                    >
                      {bookingData.contact_phone}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add-ons / Extras (SSR — leg-grouped baggage, per-segment meals/seats) */}
        {(() => {
          // Industry standard: baggage groups by LEG (one row for "Baggage
          // 15 KG · BOM → DXB · ₹3,000"). Meals and seats stay per-segment
          // because they're consumed/assigned per flight. We prefer the
          // backend-precomputed `leg_baggage_summary` and fall back to the
          // legacy flat `ssr_breakdown` when not present.
          const legBaggageRows = getLegBaggageRowsForBooking(bookingData);
          const nonBaggageRows = getNonBaggageSsrRowsForBooking(bookingData);

          // Legacy fallback: derive everything from ssr_selections when both
          // `ssr_breakdown` and `leg_baggage_summary` are empty.
          let fallback = [];
          if (!legBaggageRows.length && !nonBaggageRows.length) {
            const sel = bookingData.ssr_selections || {};
            const retSel = bookingData.return_ssr_selections || {};
            [['onward', sel], ['return', retSel]].forEach(([leg, s]) => {
              if (!s || typeof s !== 'object') return;
              ['baggage', 'meals', 'seats', 'other'].forEach((kind) => {
                (s[kind] || []).forEach((row) => {
                  fallback.push({
                    leg,
                    kind,
                    pax_ref: String(row.PaxRefNumber || ''),
                    code: String(row.Code || ''),
                    description: String(row.Description || ''),
                    amount: Number(row.Amount || 0) || 0,
                  });
                });
              });
            });
          }
          const totalAddOns =
            legBaggageRows.reduce((s, r) => s + Number(r.total_amount || 0), 0)
            + nonBaggageRows.reduce((s, r) => s + Number(r.amount || 0), 0)
            + fallback.reduce((s, r) => s + Number(r.amount || 0), 0);
          if (!legBaggageRows.length && !nonBaggageRows.length && !fallback.length) return null;

          const ICONS = { baggage: '🧳', meals: '🍽️', seats: '💺', other: '✚' };
          const KIND_LABEL = { baggage: 'Baggage', meals: 'Meal', seats: 'Seat', other: 'Other' };

          // Group everything by leg-label for top-level sections.
          const grouped = {};
          legBaggageRows.forEach((r) => {
            const k = r.leg_label || 'onward';
            grouped[k] = grouped[k] || [];
            grouped[k].push({
              _kind: 'baggage',
              description: r.description || r.code,
              pax_ref: r.pax_ref,
              amount: Number(r.total_amount || 0),
              segment_count: r.segment_count || 1,
              leg_route: r.leg_route || '',
              code: r.code,
            });
          });
          nonBaggageRows.forEach((r) => {
            const k = r.leg || 'onward';
            grouped[k] = grouped[k] || [];
            grouped[k].push({
              _kind: String(r.kind || 'other').toLowerCase(),
              description: r.description || r.code,
              pax_ref: r.pax_ref,
              amount: Number(r.amount || 0),
              segment_id: r.segment_id || '',
            });
          });
          fallback.forEach((r) => {
            const k = r.leg || 'onward';
            grouped[k] = grouped[k] || [];
            grouped[k].push({
              _kind: r.kind,
              description: r.description || r.code,
              pax_ref: r.pax_ref,
              amount: Number(r.amount || 0),
            });
          });

          const legRouteLabels = bookingData.leg_route_labels || {};
          const labelForLeg = (legKey) => {
            const route = legRouteLabels[0]
              || legRouteLabels['0']
              || legRouteLabels[legKey === 'return' ? 1 : 0]
              || (grouped[legKey] || []).find((r) => r.leg_route)?.leg_route
              || '';
            if (legKey === 'return') return route ? `Return — ${route}` : 'Return Leg';
            if (legKey === 'onward') return route ? `Onward — ${route}` : 'Onward Leg';
            return legKey;
          };

          const legOrder = Object.keys(grouped).sort(
            (a, b) => (a === 'onward' ? 0 : a === 'return' ? 1 : 2) - (b === 'onward' ? 0 : b === 'return' ? 1 : 2),
          );

          return (
            <div data-testid="addons-card" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span aria-hidden>🧳</span> Add-ons &amp; Extras
                </h2>
                <span className="text-sm font-semibold text-[#335E77]">
                  ₹{totalAddOns.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="space-y-4">
                {legOrder.map((leg) => (
                  <div key={leg} data-testid={`addons-leg-${leg}`}>
                    {legOrder.length > 1 && (
                      <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                        {labelForLeg(leg)}
                      </h3>
                    )}
                    {(grouped[leg] || []).map((r, i) => (
                      <div
                        key={`${r._kind}-${i}`}
                        data-testid={`addon-row-${leg}-${r._kind}-${i}`}
                        className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span aria-hidden className="text-lg">{ICONS[r._kind] || '•'}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {KIND_LABEL[r._kind] || r._kind}: {r.description || '—'}
                              {r._kind === 'baggage' && r.segment_count > 1 ? (
                                <span className="ml-2 text-[10px] font-semibold text-gray-500">
                                  · spans {r.segment_count} segs
                                </span>
                              ) : null}
                            </p>
                            <p className="text-xs text-gray-500">
                              {r.pax_ref ? `Passenger ${r.pax_ref}` : ''}
                            </p>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                          ₹{Number(r.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Phase C1 — Payment & Refund card (auto-hides cancellation block until cancelled). */}
        <PaymentAndRefundCard booking={bookingData} testIdPrefix="paref-b2c" />

        {/* Price Summary — itemised (base + add-ons + markup → total) */}
        {(() => {
          const total = Number(bookingData.total_amount || 0);
          const base = Number(bookingData.base_fare_total
            || (bookingData.fare_breakdown || {}).base_fare
            || (bookingData.fare_breakdown || {}).supplier_total
            || 0);
          const ssrTotal = Number(bookingData.ssr_amount
            || ((bookingData.ssr_breakdown || []).reduce((s, r) => s + (Number(r.amount) || 0), 0))
            || 0);
          const markup = Number((bookingData.markup_applied || {}).amount || 0);
          // Don't render when we have nothing to show
          if (!total && !base) return null;
          return (
            <div data-testid="price-summary-card" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-[#335E77]" />
                Price Summary
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2" data-testid="price-row-base-fare">
                  <span className="text-gray-600">Base Fare {base ? '(incl. taxes)' : ''}</span>
                  <span className="font-semibold text-gray-900">
                    ₹{base.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                {ssrTotal > 0 && (
                  <div className="flex justify-between items-center py-2" data-testid="price-row-addons">
                    <span className="text-gray-600">Add-ons (Meals / Baggage / Seats)</span>
                    <span className="font-semibold text-gray-900">
                      ₹{ssrTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {markup > 0 && isAgentAuthenticated && (
                  <div className="flex justify-between items-center py-2" data-testid="price-row-markup">
                    <span className="text-gray-600">Service Charge</span>
                    <span className="font-semibold text-gray-900">
                      ₹{markup.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between items-center" data-testid="price-row-total">
                    <span className="text-lg font-bold text-gray-900">Total Paid</span>
                    <span className="text-2xl font-bold text-[#335E77]">
                      ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tax Invoice meta (agent-only, only when invoice exists) */}
              {isAgentAuthenticated && invoiceMeta && (() => {
              const gstObj = (invoiceMeta.gst && typeof invoiceMeta.gst === 'object') ? invoiceMeta.gst : {};
              const taxableValue = invoiceMeta.taxable_value ?? gstObj.taxable_value ?? 0;
              const gstAmount = (typeof invoiceMeta.gst === 'number')
                ? invoiceMeta.gst
                : (gstObj.total_tax ?? ((gstObj.cgst_amount || 0) + (gstObj.sgst_amount || 0) + (gstObj.igst_amount || 0)));
              const totalInvoiceValue = invoiceMeta.total_invoice_value;
              return (
              <div
                data-testid="invoice-meta-block"
                className="mt-6 p-4 bg-gradient-to-br from-[#F5F8FA] to-white border border-[#335E77]/20 rounded-lg"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[#1B375B] uppercase tracking-wide">
                    GST Tax Invoice
                  </h3>
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                    Available
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Invoice #</p>
                    <p className="font-semibold text-gray-900 font-mono break-all">
                      {invoiceMeta.invoice_number || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Date</p>
                    <p className="font-semibold text-gray-900">
                      {formatDate(invoiceMeta.invoice_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Taxable Value</p>
                    <p className="font-semibold text-gray-900">
                      ₹{Number(taxableValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">GST</p>
                    <p className="font-semibold text-gray-900">
                      ₹{Number(gstAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                {totalInvoiceValue != null && (
                  <div className="mt-3 pt-3 border-t border-[#335E77]/10 flex items-center justify-between">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Invoice Value</span>
                    <span className="text-base font-bold text-[#1B375B]">
                      ₹{Number(totalInvoiceValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
              );
            })()}
          </div>
          );
        })()}

      </div>
    </div>
  );
};

export default BookingDetails;

